import { useEffect, useState } from "react";
import { adminApi, type AdminUser } from "../api/client";
import { authApi, type Invite } from "../api/auth";
import { useToast } from "../lib/toast";
import "./AdminUsersView.css";

export default function AdminUsersView() {
  const toast = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const [u, i] = await Promise.all([adminApi.listUsers(), authApi.listInvites()]);
      setUsers(u);
      setInvites(i);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const allSelected = users.length > 0 && selectedIds.size === users.length;

  const toggleSelected = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(users.map((u) => u.id)));
  };

  const toggleDisabled = async (u: AdminUser) => {
    try {
      const updated = u.is_disabled ? await adminApi.enableUser(u.id) : await adminApi.disableUser(u.id);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const remove = async (u: AdminUser) => {
    if (!confirm(`Delete ${u.email}? This permanently deletes all of their chats, projects, memories, research, and images.`)) return;
    try {
      await adminApi.deleteUser(u.id);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(u.id);
        return next;
      });
      toast.success(`${u.email} deleted`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const bulkAction = async (action: "disable" | "enable" | "delete") => {
    const ids = [...selectedIds];
    if (!ids.length || bulkBusy) return;
    if (action === "delete") {
      if (!confirm(`Delete ${ids.length} selected user${ids.length === 1 ? "" : "s"}? This permanently deletes all of their chats, projects, memories, research, and images.`)) return;
    }

    setBulkBusy(true);
    const call = action === "disable" ? adminApi.disableUser : action === "enable" ? adminApi.enableUser : adminApi.deleteUser;
    const results = await Promise.allSettled(ids.map((id) => call(id)));
    const failedIds = new Set(ids.filter((_, index) => results[index].status === "rejected"));
    const okCount = ids.length - failedIds.size;

    if (action === "delete") {
      setUsers((prev) => prev.filter((u) => !ids.includes(u.id) || failedIds.has(u.id)));
    } else {
      setUsers((prev) =>
        prev.map((u) => {
          const i = ids.indexOf(u.id);
          if (i === -1 || failedIds.has(u.id)) return u;
          const r = results[i] as PromiseFulfilledResult<AdminUser>;
          return r.value;
        }),
      );
    }
    setSelectedIds(failedIds);
    setBulkBusy(false);

    const verb = action === "disable" ? "Disabled" : action === "enable" ? "Enabled" : "Deleted";
    if (failedIds.size) {
      toast.error(`${verb} ${okCount} user${okCount === 1 ? "" : "s"}; ${failedIds.size} failed (the primary admin can't be disabled/deleted).`);
    } else {
      toast.success(`${verb} ${okCount} user${okCount === 1 ? "" : "s"}.`);
    }
  };

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Invite code copied");
    } catch {
      toast.error("Could not copy — select and copy manually");
    }
  };

  const createInvite = async () => {
    setCreating(true);
    try {
      await authApi.createInvite(inviteEmail || undefined);
      setInviteEmail("");
      const i = await authApi.listInvites();
      setInvites(i);
      toast.success("Invite created");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="view admin-users-view">Loading…</div>;

  return (
    <div className="view admin-users-view">
      <section>
        <div className="admin-section-header">
          <h1>Users</h1>
          {selectedIds.size > 0 && (
            <div className="admin-bulk-actions">
              <span>{selectedIds.size} selected</span>
              <button type="button" disabled={bulkBusy} onClick={() => bulkAction("disable")}>
                Disable
              </button>
              <button type="button" disabled={bulkBusy} onClick={() => bulkAction("enable")}>
                Enable
              </button>
              <button type="button" className="danger" disabled={bulkBusy} onClick={() => bulkAction("delete")}>
                Delete
              </button>
            </div>
          )}
        </div>
        <table className="admin-users-table">
          <thead>
            <tr>
              <th className="admin-checkbox-col">
                <input type="checkbox" aria-label="Select all users" checked={allSelected} onChange={toggleAll} />
              </th>
              <th>Email</th>
              <th>Role</th>
              <th>Verified</th>
              <th>Status</th>
              <th>Joined</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={u.is_disabled ? "admin-user-disabled" : ""}>
                <td className="admin-checkbox-col">
                  <input
                    type="checkbox"
                    aria-label={`Select ${u.email}`}
                    checked={selectedIds.has(u.id)}
                    onChange={() => toggleSelected(u.id)}
                  />
                </td>
                <td>{u.email}</td>
                <td>{u.role}</td>
                <td>{u.email_verified ? "✓" : "—"}</td>
                <td>{u.is_disabled ? "Disabled" : "Active"}</td>
                <td>{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="admin-user-actions">
                  <button type="button" onClick={() => toggleDisabled(u)}>
                    {u.is_disabled ? "Enable" : "Disable"}
                  </button>
                  <button type="button" className="danger" onClick={() => remove(u)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h1>Invites</h1>
        <div className="admin-invite-create">
          <input
            type="email"
            placeholder="Restrict to email (optional)"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <button type="button" onClick={createInvite} disabled={creating}>
            {creating ? "Creating…" : "Create invite"}
          </button>
        </div>
        <table className="admin-users-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Restricted to</th>
              <th>Status</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {invites.map((i) => (
              <tr key={i.id}>
                <td>
                  <code className="admin-invite-code" onClick={() => copyCode(i.code)} title="Click to copy">
                    {i.code}
                  </code>
                </td>
                <td>{i.email || "anyone"}</td>
                <td>{i.used_at ? "Used" : "Unused"}</td>
                <td>{new Date(i.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
